import React, { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { DescriptedDefinitionList } from '../../../_lib/components/DefinitionList';
import ContentMessage from '../../../_lib/components/ContentMessage/ContentMessage';
import { Namespace } from '../../../translation/namespace';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { Icons } from '../../../components/atoms/SVG';
import { useBlockContext } from '../../../_lib/BlockFetcher';
import {
  ADDITIONAL_EMAIL_ADDRESS,
  getAdditionalEmailAddressLabelCount,
  getTranslationNamespaceByElementType,
} from '../PersonExtendedDataBlock.utils';
import { asFetchableView } from '../../../_lib/ViewFetcher';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../../lib/typeGuards';

type PersonExtendedDataItem = {
  name: string;
  value?: string;
  url?: string;
  readProtected: boolean;
};

export type PersonExtendedDataList = {
  items: PersonExtendedDataItem[];
};

export function PersonExtendedDataList({ items }: PersonExtendedDataList) {
  const { elementType } = useBlockContext();
  const translationNamespace = getTranslationNamespaceByElementType(elementType);
  const { t } = useTranslation();

  const createTerm = (term: string, item: PersonExtendedDataItem) => {
    return {
      term,
      extendedInfoSVG: item.readProtected
        ? {
            helpText: t(`${Namespace.phrases}:readProtected.personalData`),
            tooltipPlacement: TooltipPlacement.TOP,
            svgSpriteID: Icons.CLOSED_LOCK,
          }
        : undefined,
      description: item.value || t(`${Namespace.phrases}:notYetSpecified`),
      href: item.url || undefined,
    };
  };

  const mappedItemsToTerms = items
    .map(item => {
      switch (true) {
        case item.name === 'hiddenUser':
          return null;
        case item.name.includes(ADDITIONAL_EMAIL_ADDRESS) && isNullOrUndefined(item.value):
          return null;
        case item.name.includes(ADDITIONAL_EMAIL_ADDRESS):
          return createTerm(
            t(`PersonContactData:additionalEmailAddress`, { number: getAdditionalEmailAddressLabelCount(item.name) }),
            item
          );
        default:
          return createTerm(t(`${translationNamespace}:${item.name}`), item);
      }
    })
    .filter(item => isNotNullOrUndefined(item));

  if (items && items.length > 0) {
    const hasHiddenUser: boolean = items.some(item => item.name === 'hiddenUser' && item.value === 'true');
    return (
      <div className="person-extended-data__list">
        {hasHiddenUser && (
          <p className="person-extended-data__hidden-user font-bold-15-19">
            <dfn className="invisible">{t(`${translationNamespace}:importantNotice`)}: </dfn>
            {t(`${translationNamespace}:hiddenUser`)}
          </p>
        )}
        <DescriptedDefinitionList definitionTerms={mappedItemsToTerms} />
      </div>
    );
  }

  return <ContentMessage text={t(`${translationNamespace}:noItems`)} />;
}

export const PersonExtendedDataListDefaultView = asFetchableView(PersonExtendedDataList);
export type PersonExtendedDataListDefaultView = ComponentProps<typeof PersonExtendedDataListDefaultView>;
